/**
 * Node Palette - List of draggable node types
 */

import React, { useState } from 'react';
import type { TaskTypeInfo } from '../types/pipeline';
import './NodePalette.css';

// The single node type for anything agent-driven. Rather than offering one
// palette entry per backend TaskType (which amounted to a long list of
// mostly-identical "runs one hardcoded default agent" nodes), there's just
// this one generic node - the agent (existing or brand new) is picked from
// inside the node's own config once it's on the canvas.
const AGENT_TASK_TYPE: TaskTypeInfo = {
  id: 'agent',
  label: 'Agent',
  category: 'Agent',
  description: 'Pick any agent for this node - including your own custom agents - or create a new one on the spot.',
};

// Frontend-only control-flow node - not a backend TaskType, so it's not
// returned by getTaskTypes() and must be merged in here.
const BRANCH_TASK_TYPE: TaskTypeInfo = {
  id: 'branch',
  label: 'Branch (If/Else)',
  category: 'Control Flow',
  description: 'Evaluates a single condition and routes to a true or false output',
};

// Frontend-only data-source nodes - also not backend TaskType values, so
// like BRANCH_TASK_TYPE they must be merged in here to be reachable at all.
const DATA_SOURCE_TASK_TYPES: TaskTypeInfo[] = [
  {
    id: 'data_source_item',
    label: 'Item',
    category: 'Data Source',
    description: 'Declares that this workflow expects a single item, selected at execution time',
  },
  {
    id: 'data_source_tracker',
    label: 'Tracker',
    category: 'Data Source',
    description: "Declares that this workflow expects a tracker, selected at execution time - its items plus its field schema",
  },
  {
    id: 'data_source_project',
    label: 'Project',
    category: 'Data Source',
    description: 'Declares that this workflow expects a project, selected at execution time',
  },
  {
    id: 'data_source_chapter',
    label: 'Chapter',
    category: 'Data Source',
    description: 'Declares that this workflow expects a chapter, selected at execution time',
  },
  {
    id: 'data_source_outline',
    label: 'Outline',
    category: 'Data Source',
    description: 'The chapter structure (item summaries and parent/child layers) of a tracker or chapter, selected at execution time - context only',
  },
  {
    id: 'data_source_tracker_list',
    label: 'Tracker List',
    category: 'Data Source',
    description: 'The names and ids of every tracker in a project - no items. Feed it to an agent that picks a tracker.',
  },
];

// Frontend-only search nodes - not backend TaskTypes (they're WorkflowStepTypes,
// not agent tasks), so like BRANCH_TASK_TYPE they must be merged in here. Both map
// to the same backend ItemSearchStep, distinguished by search_kind.
const ITEM_SEARCH_TASK_TYPE: TaskTypeInfo = {
  id: 'item_search',
  label: 'Item Search',
  category: 'Search',
  description: 'Finds similar items (similarity search) to feed into a downstream per_related_item step',
};

const REFERENCE_GRAPH_QUERY_TASK_TYPE: TaskTypeInfo = {
  id: 'reference_graph_query',
  label: 'Reference Graph Query',
  category: 'Search',
  description: 'Traverses the reference graph to find related items to feed into a downstream per_related_item step',
};

// Frontend-only output node - not a backend TaskType (it's a WorkflowStepType, not an agent
// task), so like the search nodes above it must be merged in here. Maps to CreateReferenceStep.
const CREATE_REFERENCE_TASK_TYPE: TaskTypeInfo = {
  id: 'create_reference',
  label: 'Create Reference',
  category: 'Output',
  description: 'Creates a trace/reference between the source item and a related item discovered by an upstream Item Search step',
};

// Frontend-only output node - not a backend TaskType (it's a WorkflowStepType, not an agent
// task), so like CREATE_REFERENCE_TASK_TYPE it must be merged in here. Maps to PushbackStep.
const PUSHBACK_TASK_TYPE: TaskTypeInfo = {
  id: 'pushback',
  label: 'Pushback',
  category: 'Output',
  description: 'Creates a draft, approval-tracked request to push a value back into a Codebeamer field',
};

// Frontend-only output node, like PUSHBACK_TASK_TYPE. Maps to CreateItemStep.
const CREATE_ITEM_TASK_TYPE: TaskTypeInfo = {
  id: 'create_item',
  label: 'Create Item',
  category: 'Output',
  description: 'Creates a new item in a target tracker, with its fields mapped from upstream outputs',
};

const TASK_TYPES: TaskTypeInfo[] = [
  AGENT_TASK_TYPE,
  ...DATA_SOURCE_TASK_TYPES,
  ITEM_SEARCH_TASK_TYPE,
  REFERENCE_GRAPH_QUERY_TASK_TYPE,
  CREATE_REFERENCE_TASK_TYPE,
  PUSHBACK_TASK_TYPE,
  CREATE_ITEM_TASK_TYPE,
  BRANCH_TASK_TYPE,
];

export const NodePalette: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  const categories = ['All', ...new Set(TASK_TYPES.map(t => t.category))];

  const filteredTasks =
    selectedCategory === 'All'
      ? TASK_TYPES
      : TASK_TYPES.filter(t => t.category === selectedCategory);

  const onDragStart = (event: React.DragEvent, task: TaskTypeInfo) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(task));
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div className="node-palette">
      <div className="node-palette-header">
        <h3>Task Nodes</h3>
        <span className="text-xs text-secondary">{TASK_TYPES.length} available</span>
      </div>

      <div className="category-tabs">
        {categories.map(cat => (
          <button
            key={cat}
            className={`category-tab ${cat === selectedCategory ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="task-list">
        {filteredTasks.map(task => (
          <div
            key={task.id}
            className="task-item"
            draggable
            onDragStart={(e) => onDragStart(e, task)}
            title={task.description}
          >
            <div className="task-info">
              <div className="task-label">{task.label}</div>
              <div className="task-category">{task.category}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
